import { ShareIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import { toast } from 'sonner';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

export interface ShareIconButtonProps {
  /** Custom share handler. When omitted, copies the current page URL to the clipboard and toasts. */
  onShare?: () => void;
  /** Accessible name AND tooltip text. Defaults to "Share". */
  label?: string;
  /** Additional classes (e.g. sizing override). */
  className?: string;
  'data-testid'?: string;
}

export function ShareIconButton({
  onShare,
  label = 'Share',
  className,
  'data-testid': testId = 'share-button',
}: ShareIconButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // safe in headers; required for clickable list rows
    if (onShare) {
      onShare();
      return;
    }
    navigator.clipboard
      ?.writeText(window.location.href)
      .then(() => toast.success('Copied URL to clipboard'))
      .catch(() => toast.error('Failed to copy URL'));
  };

  return (
    <Tooltip title={label}>
      <span className="inline-flex">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'text-chariot-text-secondary hover:text-chariot-text-primary shrink-0',
            className
          )}
          onClick={handleClick}
          aria-label={label}
          data-testid={testId}
        >
          <ShareIcon className="size-4" />
        </Button>
      </span>
    </Tooltip>
  );
}
